// questionBankItems.js
// Per-type item library for the Question bank. Each row stores ONE
// standalone question (templateType ∈ QBANK_ITEM_TYPES). Payload shape is
// the same as a template entry inside mcq_exercises.payload.templates,
// so the existing per-type sub-editors can render either form without
// translation. Phase 1 stores the item; later phases will let sets and
// curated revision exercises import items via sourceRef.

import {
  QUESTION_DISCIPLINES,
  MCQ_DIFFICULTIES,
  emptyTemplateOfType,
  countClozeMarkers,
} from './mcq';

export const QBANK_ITEM_TYPES = [
  'mcq', 'true-false', 'multi-mcq', 'cloze',
  'ordering', 'bucket-sort', 'matching',
  'fixed', 'calculation',
];

export const QBANK_ITEM_TYPE_LABELS = {
  'mcq': 'Multiple choice',
  'true-false': 'True / false',
  'multi-mcq': 'Multi-select',
  'cloze': 'Fill in the blanks',
  'ordering': 'Put in order',
  'bucket-sort': 'Sort into buckets',
  'matching': 'Match pairs',
  // 'fixed' = lecturer writes one question with one hard-coded number
  // and a tolerance; every student sees the same prompt and answer.
  // 'calculation' = formula + variables; the engine samples fresh
  // numbers each play and computes the expected answer, so every
  // student sees a different version of the same template.
  'fixed': 'Fixed answer calculation',
  'calculation': 'Auto-generated calculation',
};

export const QBANK_ITEM_TYPE_ICONS = {
  'mcq': 'bi-ui-radios',
  'true-false': 'bi-toggle-on',
  'multi-mcq': 'bi-ui-checks',
  'cloze': 'bi-input-cursor-text',
  'ordering': 'bi-sort-down',
  'bucket-sort': 'bi-grid-3x3-gap',
  'matching': 'bi-arrow-left-right',
  'fixed': 'bi-123',
  'calculation': 'bi-calculator',
};

// Groups that collapse multiple types under a single library tab. The
// user-facing "Drag and drop" library spans ordering + bucket-sort +
// matching; the rest are 1-to-1.
export const QBANK_ITEM_GROUPS = [
  { key: 'mcq',           label: 'Multiple choice',    icon: 'bi-ui-radios',          types: ['mcq'] },
  { key: 'true-false',    label: 'True / false',       icon: 'bi-toggle-on',          types: ['true-false'] },
  { key: 'multi-mcq',     label: 'Multi-select',       icon: 'bi-ui-checks',          types: ['multi-mcq'] },
  { key: 'cloze',         label: 'Fill in the blanks', icon: 'bi-input-cursor-text',  types: ['cloze'] },
  { key: 'drag-and-drop', label: 'Drag and drop',      icon: 'bi-grid-3x3-gap',       types: ['ordering', 'bucket-sort', 'matching'] },
  { key: 'calculation',   label: 'Calculation',        icon: 'bi-calculator',         types: ['calculation', 'fixed'] },
];

const PROMPT_EXCERPT_LENGTH = 140;

export function promptExcerptFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return '';
  const raw = String(payload.prompt || '').replace(/\s+/g, ' ').trim();
  if (!raw) return '';
  return raw.length > PROMPT_EXCERPT_LENGTH
    ? `${raw.slice(0, PROMPT_EXCERPT_LENGTH - 1)}…`
    : raw;
}

export const emptyQuestionBankItem = (type = 'mcq') => ({
  id: '',
  templateType: QBANK_ITEM_TYPES.includes(type) ? type : 'mcq',
  category: '',
  difficulty: '',
  disciplines: [],
  status: 'draft',
  // Payload shape is the existing template shape; emptyTemplateOfType
  // also stamps a template id we don't actually persist server-side
  // (the row id is canonical). We keep it in client state for React
  // keying inside any sub-fields the editor renders.
  payload: emptyTemplateOfType(type),
});

function normaliseDisciplines(input) {
  return Array.isArray(input)
    ? input.filter((d) => QUESTION_DISCIPLINES.includes(d))
    : [];
}

export function normaliseQuestionBankItem(input) {
  if (!input || typeof input !== 'object') return emptyQuestionBankItem();
  const type = QBANK_ITEM_TYPES.includes(input.templateType) ? input.templateType : 'mcq';
  const payload = input.payload && typeof input.payload === 'object'
    ? { ...input.payload, templateType: type }
    : emptyTemplateOfType(type);
  const difficultyRaw = String(input.difficulty || '').trim().toLowerCase();
  return {
    id: input.id || '',
    templateType: type,
    category: String(input.category || '').trim(),
    difficulty: MCQ_DIFFICULTIES.includes(difficultyRaw) ? difficultyRaw : '',
    disciplines: normaliseDisciplines(input.disciplines),
    status: input.status === 'published' || input.status === 'archived' ? input.status : 'draft',
    payload,
    promptExcerpt: input.promptExcerpt || promptExcerptFromPayload(payload),
    updatedAt: input.updatedAt || input.updated_at || null,
    createdAt: input.createdAt || input.created_at || null,
  };
}

// Validate a single question-bank item. Reuses the same per-type logic
// as the existing set-level validator in src/lib/mcq.js but operates
// on one template at a time. Returns an array of { key, message }.
export function validateQuestionBankItem(item) {
  const norm = normaliseQuestionBankItem(item);
  const issues = [];
  const t = norm.payload;
  if (!String(t.prompt || '').trim()) {
    issues.push({ key: 'prompt', message: 'Prompt is required.' });
  }
  if (t.templateType === 'true-false') {
    if (t.correctAnswer !== true && t.correctAnswer !== false) {
      issues.push({ key: 'tf', message: 'Pick True or False as the answer.' });
    }
    return issues;
  }
  if (t.templateType === 'cloze') {
    const markers = countClozeMarkers(t.prompt);
    const blanks = Array.isArray(t.blanks) ? t.blanks : [];
    if (!blanks.length) issues.push({ key: 'blanks', message: 'Add at least one blank.' });
    if (markers !== blanks.length) {
      issues.push({ key: 'markers', message: `Prompt has ${markers} {{n}} markers but ${blanks.length} blanks are defined.` });
    }
    blanks.forEach((b, bi) => {
      const answers = (Array.isArray(b?.answers) ? b.answers : []).filter((a) => String(a).trim());
      if (!answers.length) {
        issues.push({ key: `blank-${bi}`, message: `Blank ${bi + 1} needs at least one accepted answer.` });
      }
    });
    return issues;
  }
  if (t.templateType === 'ordering') {
    const items = Array.isArray(t.items) ? t.items : [];
    if (items.length < 2) issues.push({ key: 'items', message: 'Add at least two items to order.' });
    items.forEach((it, i) => {
      if (!String(it?.text || '').trim()) issues.push({ key: `ord-${i}`, message: `Item ${i + 1}: text is required.` });
    });
    return issues;
  }
  if (t.templateType === 'bucket-sort') {
    const buckets = Array.isArray(t.buckets) ? t.buckets : [];
    const items = Array.isArray(t.items) ? t.items : [];
    if (buckets.length < 2) issues.push({ key: 'buckets', message: 'Add at least two buckets.' });
    if (items.length < 2) issues.push({ key: 'items', message: 'Add at least two items to sort.' });
    buckets.forEach((b, i) => {
      if (!String(b?.label || '').trim()) issues.push({ key: `bucket-${i}`, message: `Bucket ${i + 1}: label is required.` });
    });
    items.forEach((it, i) => {
      if (!String(it?.text || '').trim()) issues.push({ key: `sort-${i}`, message: `Item ${i + 1}: text is required.` });
    });
    return issues;
  }
  if (t.templateType === 'matching') {
    const pairs = Array.isArray(t.pairs) ? t.pairs : [];
    if (pairs.length < 2) issues.push({ key: 'pairs', message: 'Add at least two pairs.' });
    pairs.forEach((p, i) => {
      if (!String(p?.left || '').trim()) issues.push({ key: `pair-${i}-l`, message: `Pair ${i + 1}: left side is required.` });
      if (!String(p?.right || '').trim()) issues.push({ key: `pair-${i}-r`, message: `Pair ${i + 1}: right side is required.` });
    });
    return issues;
  }
  if (t.templateType === 'fixed') {
    const expected = Number(t.expectedAnswer);
    if (!Number.isFinite(expected)) {
      issues.push({ key: 'expected', message: 'Expected numeric answer is required.' });
    }
    return issues;
  }
  if (t.templateType === 'calculation') {
    if (!String(t.formula || '').trim()) {
      issues.push({ key: 'formula', message: 'Formula is required.' });
    }
    const variables = Array.isArray(t.variables) ? t.variables : [];
    if (!variables.length) {
      issues.push({ key: 'variables', message: 'Declare at least one variable used by the formula.' });
    }
    variables.forEach((v, vi) => {
      if (!String(v?.name || '').trim()) {
        issues.push({ key: `variable-${vi}-name`, message: `Variable ${vi + 1}: name is required.` });
      }
      const min = Number(v?.min);
      const max = Number(v?.max);
      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        issues.push({ key: `variable-${vi}-range`, message: `Variable "${v?.name || vi + 1}": min and max must be numbers.` });
      } else if (max < min) {
        issues.push({ key: `variable-${vi}-range`, message: `Variable "${v?.name || vi + 1}": max must be ≥ min.` });
      }
    });
    return issues;
  }
  // mcq + multi-mcq
  const options = Array.isArray(t.options) ? t.options : [];
  if (options.length < 2) issues.push({ key: 'options', message: 'Add at least two options.' });
  options.forEach((o, i) => {
    if (!String(o || '').trim()) issues.push({ key: `opt-${i}`, message: `Option ${i + 1} is empty.` });
  });
  if (t.templateType === 'multi-mcq') {
    const correctIndices = Array.isArray(t.correctIndices) ? t.correctIndices : [];
    if (!correctIndices.length) issues.push({ key: 'correct', message: 'Mark at least one option as correct.' });
  } else {
    const correctIndex = Number(t.correctIndex);
    if (!Number.isFinite(correctIndex) || correctIndex < 0 || correctIndex >= options.length) {
      issues.push({ key: 'correct', message: 'Pick a correct answer.' });
    }
  }
  return issues;
}

// Used by the library UI to find the group a given item type belongs to
// — e.g. ordering → 'drag-and-drop'.
export function groupKeyForType(type) {
  const group = QBANK_ITEM_GROUPS.find((g) => g.types.includes(type));
  return group ? group.key : null;
}
